// Step 4 — Mapping: who fills each field of the target's first step, and how Aljex's value is built.
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';

import { diApi } from '../../api';
import type { FieldBinding, MappedValue, UpdatePolicy } from '../../types';
import type { StepProps } from '../stepProps';
import { ExprChips, ExprEditor, acceptedResourceTypes, exprComplete, exprPrimaryField } from './MappingParts';
import { CatalogueFieldPicker, FillBar, PanelHead, UPDATE_POLICY_LABEL, errMsg, mappedProcess, unwrap } from './SharedParts';

type TargetField = { uid: string; label: string; type: string; required: boolean; options?: string[] };
type Preview = { matched: boolean; byUid: Map<string, MappedValue> };

const COLORS = { aljex: 'primary.main', ops: 'secondary.light', unset: 'warning.light' } as const;

export default function MappingStep(props: StepProps) {
  const { spec, onChange, processes, catalogue, lookups, clientUid, pipeline, sampleRecordId } = props;
  const process = mappedProcess(spec, processes);
  const firstStep = process?.firstStep || null;
  const fields: TargetField[] = firstStep?.fields || [];
  const [expanded, setExpanded] = useState<string | null>(null);

  const bindingByUid = useMemo(() => new Map(spec.bindings.map((b) => [b.fieldUid, b])), [spec.bindings]);

  const upsert = (f: TargetField, patch: Partial<FieldBinding>) => {
    const idx = spec.bindings.findIndex((b) => b.fieldUid === f.uid);
    const base: FieldBinding =
      idx >= 0 ? spec.bindings[idx] : { fieldUid: f.uid, label: f.label, fieldType: f.type, required: f.required, owner: 'OPERATIONS' };
    const next: FieldBinding = { ...base, label: f.label, fieldType: f.type, required: f.required, ...patch };
    const bindings = idx >= 0 ? spec.bindings.map((b, i) => (i === idx ? next : b)) : [...spec.bindings, next];
    onChange({ ...spec, bindings });
  };

  // ---- Sample value preview (debounced; stale answers dropped) ----
  const [preview, setPreview] = useState<Preview | null>(null);
  const [previewErr, setPreviewErr] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const specKey = JSON.stringify(spec);
  const specRef = useRef(spec);
  specRef.current = spec;
  const reqId = useRef(0);
  useEffect(() => {
    const id = ++reqId.current;
    if (!sampleRecordId) {
      setPreview(null);
      setPreviewErr(null);
      setPreviewLoading(false);
      return undefined;
    }
    setPreviewLoading(true);
    const t = setTimeout(() => {
      diApi
        .preview(clientUid, pipeline.uid, { recordId: sampleRecordId, spec: specRef.current })
        .then(unwrap)
        .then((ev) => {
          if (id !== reqId.current) return;
          setPreview({ matched: ev.matched, byUid: new Map((ev.mapped || []).map((m) => [m.fieldUid, m])) });
          setPreviewErr(null);
        })
        .catch((e) => {
          if (id !== reqId.current) return;
          setPreviewErr(errMsg(e, 'Could not preview the sample load'));
        })
        .finally(() => {
          if (id === reqId.current) setPreviewLoading(false);
        });
    }, 600);
    return () => clearTimeout(t);
  }, [clientUid, pipeline.uid, sampleRecordId, specKey]);

  if (!process) {
    return (
      <Stack spacing={3}>
        <PanelHead title="Fill the first step" />
        <Alert severity="warning">
          {spec.target.mode === 'ROUTED'
            ? 'Route at least one value to a process in Target first.'
            : 'Pick a target process in Target first.'}
        </Alert>
      </Stack>
    );
  }
  if (!firstStep || fields.length === 0) {
    return (
      <Stack spacing={3}>
        <PanelHead title="Fill the first step" />
        <Alert severity="warning">{process.name} has no first step with fields to fill.</Alert>
      </Stack>
    );
  }

  // ---- Coverage ----
  let aljex = 0;
  let ops = 0;
  fields.forEach((f) => {
    const b = bindingByUid.get(f.uid);
    if (b?.owner === 'OPERATIONS') ops += 1;
    else if (b?.owner === 'SOURCE' && exprComplete(b.expr)) aljex += 1;
  });
  const unset = fields.length - aljex - ops;
  const pct = (n: number) => `${(n / fields.length) * 100}%`;

  const fieldUids = new Set(fields.map((f) => f.uid));
  const orphans = spec.target.mode === 'SINGLE' ? spec.bindings.filter((b) => !fieldUids.has(b.fieldUid)) : [];

  const sampleCell = (f: TargetField, b: FieldBinding | undefined) => {
    const dash = (
      <Typography variant="body2" color="text.disabled">
        —
      </Typography>
    );
    if (!b || b.owner !== 'SOURCE' || !preview || !preview.matched) return dash;
    const m = preview.byUid.get(f.uid);
    if (!m) return dash;
    if (m.missing) return <Chip size="small" color="warning" variant="outlined" label={m.note || 'Empty'} />;
    return (
      <Stack spacing={0.25}>
        <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>
          {m.display || String(m.value ?? '')}
        </Typography>
        {m.note && (
          <Typography variant="caption" color="text.secondary">
            {m.note}
          </Typography>
        )}
      </Stack>
    );
  };

  return (
    <Stack spacing={3}>
      <PanelHead
        title="Fill the first step"
        subtitle={
          <>
            Each field in <b>{firstStep.name}</b> is filled from Aljex or left to Operations. Fields are linked by ID, so renaming one in
            the process doesn&apos;t break the mapping.
          </>
        }
      />
      {spec.target.mode === 'ROUTED' && (
        <Alert severity="info">
          Each routed process has its own first step. This shows <b>{process.name}</b>, the first routed process; map the others once their
          fields line up.
        </Alert>
      )}

      <Stack spacing={1}>
        <Box
          role="img"
          aria-label={`${aljex} fields from Aljex, ${ops} by Operations${unset ? `, ${unset} unmapped` : ''}`}
          sx={{ display: 'flex', height: 10, borderRadius: 5, overflow: 'hidden', bgcolor: 'divider' }}
        >
          <Box sx={{ width: pct(aljex), bgcolor: COLORS.aljex }} />
          <Box sx={{ width: pct(ops), bgcolor: COLORS.ops }} />
          <Box sx={{ width: pct(unset), bgcolor: COLORS.unset }} />
        </Box>
        <Stack direction="row" spacing={3} alignItems="center">
          {[
            { n: aljex, text: 'from Aljex', color: COLORS.aljex, show: true },
            { n: ops, text: 'by Operations', color: COLORS.ops, show: true },
            { n: unset, text: 'unmapped', color: COLORS.unset, show: unset > 0 }
          ]
            .filter((l) => l.show)
            .map((l) => (
              <Stack key={l.text} direction="row" spacing={0.75} alignItems="center">
                <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: l.color }} />
                <Typography variant="body2">
                  <b>{l.n}</b> {l.text}
                </Typography>
              </Stack>
            ))}
        </Stack>
        <Typography variant="caption" color="text.secondary">
          Percentages show how often Aljex has a value, across the loads received so far.
        </Typography>
      </Stack>

      {!sampleRecordId && <Alert severity="info">Pick a sample load to preview values.</Alert>}
      {sampleRecordId && preview && !preview.matched && (
        <Alert severity="info">
          The sample load doesn&apos;t match the filter, so there are no values to preview. Pick another sample load.
        </Alert>
      )}
      {previewErr && <Alert severity="warning">{previewErr}</Alert>}
      {orphans.length > 0 && (
        <Alert
          severity="warning"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => onChange({ ...spec, bindings: spec.bindings.filter((b) => fieldUids.has(b.fieldUid)) })}
            >
              Remove
            </Button>
          }
        >
          {orphans.length} mapping{orphans.length > 1 ? 's point' : ' points'} at fields that are no longer in {firstStep.name}:{' '}
          {orphans.map((o) => o.label).join(', ')}.
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width="24%">Field</TableCell>
              <TableCell width={190}>Filled by</TableCell>
              <TableCell>From Aljex</TableCell>
              <TableCell width="20%">
                <Stack direction="row" spacing={1} alignItems="center">
                  <span>Sample value</span>
                  {previewLoading && <CircularProgress size={12} />}
                </Stack>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {fields.map((f) => {
              const b = bindingByUid.get(f.uid);
              const owner = b?.owner || null;
              const open = expanded === f.uid && owner === 'SOURCE';
              const primary = exprPrimaryField(b?.expr);
              const cat = primary ? catalogue.find((c) => c.field === primary) : undefined;
              let source: JSX.Element;
              if (owner === 'OPERATIONS') {
                source = (
                  <Typography variant="body2" color="text.secondary">
                    Entered by Operations
                  </Typography>
                );
              } else if (owner !== 'SOURCE') {
                source = <Chip size="small" color="warning" variant="outlined" label="Choose who fills it" />;
              } else if (!b?.expr || (b.expr.kind === 'field' && !b.expr.field)) {
                source = (
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Chip size="small" color="warning" variant="outlined" label="Choose an Aljex field" />
                    <CatalogueFieldPicker
                      catalogue={catalogue}
                      value=""
                      onChange={(field) => {
                        if (!field) return;
                        if (f.type !== 'RESOURCE') return upsert(f, { expr: { kind: 'field', field } });
                        // A resource field is filled by matching the value to a resource; open the details to choose the match.
                        const type = acceptedResourceTypes(lookups, f)[0];
                        upsert(f, {
                          expr: {
                            kind: 'resource',
                            of: { kind: 'field', field },
                            resourceTypeId: type?._id || '',
                            matchField: '',
                            normalise: null
                          }
                        });
                        setExpanded(f.uid);
                      }}
                      commitOnType={false}
                      width={220}
                    />
                    <Button size="small" onClick={() => setExpanded(open ? null : f.uid)}>
                      More options
                    </Button>
                  </Stack>
                );
              } else {
                source = (
                  <Stack spacing={0.75}>
                    <ExprChips expr={b.expr} lookups={lookups} />
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      {cat && <FillBar pct={cat.fillPct} />}
                      {!exprComplete(b.expr) && <Chip size="small" color="warning" variant="outlined" label="Incomplete" />}
                      <Button size="small" onClick={() => setExpanded(open ? null : f.uid)} aria-expanded={open}>
                        {open ? 'Hide details' : 'Details'}
                      </Button>
                    </Stack>
                  </Stack>
                );
              }
              return (
                <Fragment key={f.uid}>
                  <TableRow sx={open ? { '& > td': { borderBottom: 0 } } : undefined}>
                    <TableCell>
                      <Typography variant="subtitle2">{f.label}</Typography>
                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.25 }}>
                        <Typography variant="caption" color="text.secondary">
                          {f.type}
                        </Typography>
                        {f.required && <Chip size="small" label="Required" sx={{ height: 18, fontSize: '0.7rem' }} />}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <ToggleButtonGroup
                        exclusive
                        size="small"
                        color="primary"
                        value={owner}
                        onChange={(_, v) => v && upsert(f, { owner: v })}
                        aria-label={`Who fills ${f.label}`}
                      >
                        <ToggleButton value="SOURCE" sx={{ px: 1.5, py: 0.25 }}>
                          Aljex
                        </ToggleButton>
                        <ToggleButton value="OPERATIONS" sx={{ px: 1.5, py: 0.25 }}>
                          Operations
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </TableCell>
                    <TableCell>{source}</TableCell>
                    <TableCell>{sampleCell(f, b)}</TableCell>
                  </TableRow>
                  {open && b && (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ bgcolor: 'action.hover' }}>
                        <Grid container spacing={3} sx={{ py: 1 }}>
                          <Grid item xs={8}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                              How the value is built
                            </Typography>
                            <ExprEditor
                              expr={b.expr}
                              onChange={(expr) => upsert(f, { expr })}
                              catalogue={catalogue}
                              lookups={lookups}
                              clientUid={clientUid}
                              sourceUid={pipeline.sourceUid}
                              targetField={f}
                            />
                          </Grid>
                          <Grid item xs={4}>
                            <Stack spacing={2}>
                              <FormControl size="small" fullWidth>
                                <InputLabel id={`upd-${f.uid}`}>When Aljex changes it</InputLabel>
                                <Select
                                  labelId={`upd-${f.uid}`}
                                  label="When Aljex changes it"
                                  value={b.onUpdate || spec.sync.defaultOnUpdate}
                                  onChange={(e) => {
                                    const v = e.target.value as UpdatePolicy;
                                    upsert(f, { onUpdate: v === spec.sync.defaultOnUpdate ? undefined : v });
                                  }}
                                >
                                  {(Object.keys(UPDATE_POLICY_LABEL) as UpdatePolicy[]).map((p) => (
                                    <MenuItem key={p} value={p}>
                                      {UPDATE_POLICY_LABEL[p]}
                                      {p === spec.sync.defaultOnUpdate ? ' (pipeline default)' : ''}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              <FormControl size="small" fullWidth>
                                <InputLabel id={`miss-${f.uid}`}>If missing</InputLabel>
                                <Select
                                  labelId={`miss-${f.uid}`}
                                  label="If missing"
                                  value={b.onMissing || 'LEAVE_EMPTY'}
                                  onChange={(e) => upsert(f, { onMissing: e.target.value as 'LEAVE_EMPTY' | 'FLAG' })}
                                >
                                  <MenuItem value="LEAVE_EMPTY">Leave it empty</MenuItem>
                                  <MenuItem value="FLAG">Leave it empty and flag it</MenuItem>
                                </Select>
                              </FormControl>
                            </Stack>
                          </Grid>
                        </Grid>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}
